import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ContractorsApi, type FetchContractorsParams } from '@/api/domains/contractors.api';
import { ContractorsQueryKeys } from '@/api/domains/contractors.query-keys';
import type { Contractor } from '@/api/domains/contractors.types';
import { useDebouncedCallback } from '@/shared/lib/react';

type UseContractorsSelectParams = {
  fetchParams?: FetchContractorsParams;
  shouldInitialPreload?: boolean;
  idToExclude?: Contractor['id'];
};

const MIN_SEARCH_LENGTH = 2;
const defaultOptions: Contractor[] = [];

export const useContractorsSelect = (params: UseContractorsSelectParams = {}) => {
  const { shouldInitialPreload = false, fetchParams, idToExclude } = params;

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSetSearchQuery, cancel] = useDebouncedCallback(setSearchQuery, { wait: 300 });

  const requestParams: Partial<FetchContractorsParams> = {
    ...fetchParams,
    query: searchQuery.toLocaleLowerCase(),
    limit: 50,
  };

  const { isInitialLoading, data } = useQuery({
    queryKey: ContractorsQueryKeys.getContractorsTreeKey(requestParams),
    queryFn: () => ContractorsApi.fetchContractors(requestParams),
    enabled: shouldInitialPreload || searchQuery.length >= MIN_SEARCH_LENGTH,
    staleTime: Infinity,
  });

  const options = data?.items.filter(({ id }) => id !== idToExclude) || defaultOptions;

  const searchFn = (inputValue: string) => {
    cancel();
    debouncedSetSearchQuery(() => inputValue);
  };

  return {
    searchFn,
    options,
    isInitialLoading,
  };
};
